import { lazy, Suspense, type ComponentProps, type ComponentType } from 'react';
import {
  Navigate,
  Route,
  type Params,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AuthGate } from './AuthGate';
import { CaseShell } from './CaseShell';
import { ExportsScreen } from '../screens/ExportsScreen';
import { CaseWorkspaceShell } from './CaseWorkspaceShell';
import { CollectionSetLegacyRedirect } from './CollectionSetLegacyRedirect';
import { NotFoundScreen } from './NotFoundScreen';
import { RouteError } from './RouteError';
import { LoginScreen } from '../screens/LoginScreen';
import { Skeleton } from '../components/shared/Skeleton';
import { useCase } from '../contexts/CaseContext';

// #915 — every screen is its own chunk, so a reviewer who only opens the
// table and drawer never downloads the claims-matrix, chronology, discovery,
// or graph stacks. Shared chrome (AuthGate, CaseShell, CaseWorkspaceShell)
// and the two terminal screens (login, not-found) stay eager: login is the
// first paint and not-found is tiny, so neither should pay a chunk round trip.
//
// The typed `C` keeps each wrapper's real prop shape (e.g.
// `IncomingRequestsScreen embedded`) instead of widening every screen.
// `any` is React.lazy's own constraint; prop types still flow through
// `ComponentProps<C>` at every call site.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyScreen<C extends ComponentType<any>>(
  loader: () => Promise<{ default: C }>,
) {
  const Lazy = lazy(loader);
  return function LazyScreen(props: ComponentProps<C>) {
    return (
      <Suspense fallback={<RouteLoading />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}

// Mirrors the shared Skeleton language used by the tables (#244 / #277) so a
// route transition reads as "loading the screen", not a blank flash.
function RouteLoading() {
  return (
    <div
      data-testid="route-loading"
      aria-busy="true"
      className="p-8 space-y-4 max-w-5xl mx-auto"
    >
      <Skeleton variant="card" count={3} />
    </div>
  );
}

const CasesScreen = lazyScreen(() =>
  import('../screens/CasesScreen').then((m) => ({ default: m.CasesScreen })),
);
const LitDeskScreen = lazyScreen(() =>
  import('../screens/LitDeskScreen').then((m) => ({ default: m.LitDeskScreen })),
);
const PleadingsScreen = lazyScreen(() =>
  import('../screens/PleadingsScreen').then((m) => ({ default: m.PleadingsScreen })),
);
const PartiesScreen = lazyScreen(() =>
  import('../screens/PartiesScreen').then((m) => ({ default: m.PartiesScreen })),
);
const ChronologyScreen = lazyScreen(() =>
  import('../screens/ChronologyScreen').then((m) => ({ default: m.ChronologyScreen })),
);
const ClaimsMatrixScreen = lazyScreen(() =>
  import('../screens/ClaimsMatrixScreen').then((m) => ({ default: m.ClaimsMatrixScreen })),
);
const ClaimsMatrixPickerScreen = lazyScreen(() =>
  import('../screens/ClaimsMatrixPickerScreen').then((m) => ({
    default: m.ClaimsMatrixPickerScreen,
  })),
);
const ClaimsMatrixV2Screen = lazyScreen(() =>
  import('../screens/ClaimsMatrixV2Screen').then((m) => ({
    default: m.ClaimsMatrixV2Screen,
  })),
);
const ClaimsMatrixV2PickerScreen = lazyScreen(() =>
  import('../screens/ClaimsMatrixV2PickerScreen').then((m) => ({
    default: m.ClaimsMatrixV2PickerScreen,
  })),
);
const ReviewScreen = lazyScreen(() =>
  import('../screens/ReviewScreen').then((m) => ({ default: m.ReviewScreen })),
);
const DiscoveryWorkspaceScreen = lazyScreen(() =>
  import('../screens/DiscoveryWorkspaceScreen').then((m) => ({
    default: m.DiscoveryWorkspaceScreen,
  })),
);
const IncomingRequestsScreen = lazyScreen(() =>
  import('../screens/IncomingRequestsScreen').then((m) => ({
    default: m.IncomingRequestsScreen,
  })),
);
const IssuesScreen = lazyScreen(() =>
  import('../screens/IssuesScreen').then((m) => ({ default: m.IssuesScreen })),
);
const MembersScreen = lazyScreen(() =>
  import('../screens/MembersScreen').then((m) => ({ default: m.MembersScreen })),
);
const CaseChatScreen = lazyScreen(() =>
  import('../screens/CaseChatScreen').then((m) => ({ default: m.CaseChatScreen })),
);
const GraphSearchScreen = lazyScreen(() =>
  import('../screens/GraphSearchScreen').then((m) => ({ default: m.GraphSearchScreen })),
);

// Tiny helper for case-scoped redirects. Used for legacy URLs that have
// since moved (e.g. /admin → tab home, /lit-desk → tab home).
// `to` also gets the matched params, so a moved route can carry an id across
// (e.g. /chat/<id> → /workbench/<id>).
function CaseRedirect({ to }: { to: (caseKey: string, params: Readonly<Params<string>>) => string }) {
  const params = useParams();
  const { caseKey } = params;
  if (!caseKey) return <Navigate to="/cases" replace />;
  return <Navigate to={to(caseKey, params)} replace />;
}

// The Workbench shipped to its pilot as "Case Chat" at /cases/:caseKey/chat and
// /chat/<chatId>. Those bookmarks land on the same chat under /workbench. The
// id comes back decoded from useParams, so it is re-encoded on the way out.
// Exported for test (routing/LegacyChatRedirect.test.tsx).
export function LegacyChatRedirect() {
  return (
    <CaseRedirect
      to={(k, p) => `/cases/${k}/workbench${p.chatId ? `/${encodeURIComponent(p.chatId)}` : ''}`}
    />
  );
}

// Claims Matrix landing (#620). Same-route param branch mirroring Document
// Discovery's cards-vs-table split (ReviewScreen `showCardsLanding`): no
// `?complaint` → the operative-complaint tile picker; `?complaint=<source_doc_id>`
// present → that complaint's scoped matrix. ClaimsMatrixScreen assumes a real
// `?complaint`, so this guard is what guarantees it never renders scope-less.
// #1093 adds a third arm: `?pleading=<case_pleadings.id>` is the new Cause of
// Action → Contention → Evidence matrix, gated per case on
// `cases.claims_matrix_v2_enabled_at`.
//
// The gate is a COLUMN, not `import.meta.env.VITE_*`. The build-time flag is the
// only mechanism the frontend otherwise has, and it would make "turn it back
// off" a rebuild, an S3 sync and a CloudFront invalidation applied to every
// matter at once — while the preview extraction this pairs with is gated to one
// named case server-side. Different blast radii for the screen and the data
// defeat the point of a preview.
//
// Flag off and `?pleading` present is a real state — a pasted link, or a flag
// switched back while someone had the tab open. It falls through to the picker
// rather than rendering a matrix the case is not supposed to show yet.
// Exported for test. The redirect it serves is the one rule on this route with
// no other guard: the SCREEN's flag check is covered by
// ClaimsMatrixV2Screen.setup.test.tsx, but the screen is not what mounts when
// the flag is off — this is.
export function stripParam(loc: { pathname: string; search: string }, key: string): string {
  const next = new URLSearchParams(loc.search);
  next.delete(key);
  next.delete('claim');
  return `${loc.pathname}${next.size ? `?${next.toString()}` : ''}`;
}

export function ClaimsMatrixRoute() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const caseData = useCase();
  if (searchParams.get('pleading')) {
    if (caseData.claims_matrix_v2_enabled_at) return <ClaimsMatrixV2Screen />;
    // Flag off but the param is still on the URL — a pasted link, or a flag
    // switched back. Strip it rather than just ignoring it: the breadcrumb arm
    // keys off `?pleading` alone, so leaving it renders a phantom "Pleading"
    // crumb that never resolves, marked `current`, above a parent linking to
    // the page you are already on. The v2 screen would clean this up itself,
    // but it is not the component that mounts here.
    return <Navigate to={stripParam(location, 'pleading')} replace />;
  }
  // The no-param arm: the picker. #1094 gives it a second, flag-gated version,
  // and THIS BRANCH is what keeps that ticket inert on merge.
  //
  // Unlike the matrix arm above, the two pickers cannot be told apart by a URL
  // param — the picker is what renders when there is no param at all. So the
  // branch has to be the per-case flag, and without it a picker reading
  // `case_pleadings` would ship to the landing page of every matter, against
  // tables that do not exist until #1113.
  //
  // Nothing else is needed to make merging safe: `claims_matrix_v2_enabled_at`
  // is added by expand.sql, so until that lands the column does not exist,
  // `handle_list_cases`'s SELECT * cannot return it, and this is `undefined`
  // on every case. After it lands it is NULL everywhere until #1102 sets it.
  if (searchParams.get('complaint')) return <ClaimsMatrixScreen />;
  return caseData.claims_matrix_v2_enabled_at ? (
    <ClaimsMatrixV2PickerScreen />
  ) : (
    <ClaimsMatrixPickerScreen />
  );
}
// Route tree. The case workspace presents four top-level tabs (Lit Desk,
// Document Collection, Case Issues, Pleadings) that share a chrome
// (case H1 + tab bar) via <CaseWorkspaceShell>. Drilled-in screens
// (Document Discovery review, Discovery Responses, the Collection
// set-detail view, Members) render outside the shell with their own
// header so they can use the full viewport.
//
// See docs/url-routing-feature.md §3 for the URL contract.
export const router = createBrowserRouter(
  createRoutesFromElements(
    // Pathless wrapper: owns the route-level error UI for every child. This is
    // where a failing lazy-screen import lands (a data router handles route
    // render errors here, not at the app's React ErrorBoundary).
    <Route errorElement={<RouteError />}>
      <Route path="/login" element={<LoginScreen />} />

      <Route element={<AuthGate />}>
        <Route index element={<Navigate to="/cases" replace />} />
        <Route path="/cases" element={<CasesScreen />} />

        <Route path="/cases/:caseKey" element={<CaseShell />}>
          {/* Tabbed case workspace — shared H1 + tab nav above the outlet. */}
          <Route element={<CaseWorkspaceShell />}>
            <Route index element={<LitDeskScreen />} />
            <Route path="collection" element={<IncomingRequestsScreen embedded />} />
            <Route path="issues" element={<IssuesScreen />} />
            <Route path="pleadings" element={<PleadingsScreen />} />
            <Route path="parties" element={<PartiesScreen />} />
          </Route>

          {/* Drilled-in screens — render their own Header, no tabs. */}
          <Route path="documents/review" element={<ReviewScreen />} />
          {/* Background export jobs (#763). Case-scoped and under AuthGate via
              CaseShell — an artifact download must never be reachable
              unauthenticated. */}
          <Route path="exports" element={<ExportsScreen />} />
          <Route path="collection/sets/:setId" element={<CollectionSetLegacyRedirect />} />
          <Route path="members" element={<MembersScreen />} />
          {/* Chronology — reached from the Lit Desk "Chronology" tile (Decision 8). */}
          <Route path="chronology" element={<ChronologyScreen />} />
          {/* Claims Matrix — reached from the Lit Desk "Claims Matrix" tile (#564).
              #620: no ?complaint → the operative-complaint tile picker; a
              ?complaint=<source_doc_id> → that complaint's scoped matrix. */}
          <Route path="claims-matrix" element={<ClaimsMatrixRoute />} />

          {/* Graph-RAG chat (Phase 1 of the graph-rag query feature).
              Backed by POST /cases/{id}/ai/graph-search (api/routes/graph_rag.py),
              which proxies to the private graph-rag service with the user's
              auth context. Available on any case that already has a graph
              indexed in graph-rag — the screen calls /availability on mount. */}
          <Route path="ai/graph-search" element={<GraphSearchScreen />} />

          {/* Workbench (internally "Case Chat") — per-case conversations,
              full viewport like Chronology. /workbench is a blank new chat;
              /workbench/<id> is that chat. The optional param keeps the screen
              mounted when the first send rewrites the URL. Reached from the
              Lit Desk "Workbench" tile (pilot-gated by
              GET /cases/{id}/chat/availability); a direct URL for a user
              without access shows the unavailable panel. */}
          <Route path="workbench/:chatId?" element={<CaseChatScreen />} />

          {/* Discovery Responses — all sub-paths render the same screen,
              which reads its current view from the URL. */}
          <Route path="discovery/responses" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/all" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/files" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/uploads" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake/:intakeId" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake/:intakeId/review" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/batch/:batchId" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/compare" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/history" element={<DiscoveryWorkspaceScreen />} />

          {/* Legacy redirects — keep deep bookmarks working for one release. */}
          {/* The pilot's Case Chat URLs, from before it became Workbench. */}
          <Route path="chat/:chatId?" element={<LegacyChatRedirect />} />
          <Route
            path="documents"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="admin"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="lit-desk"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="lit-desk/document-discovery"
            element={
              <CaseRedirect
                to={(k) => `/cases/${k}/documents/review?direction=received`}
              />
            }
          />
          <Route
            path="lit-desk/discovery-responses"
            element={<CaseRedirect to={(k) => `/cases/${k}/discovery/responses`} />}
          />
          <Route
            path="lit-desk/*"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="discovery/requests"
            element={<CaseRedirect to={(k) => `/cases/${k}/collection`} />}
          />
          <Route
            path="discovery/requests/sets/:setId"
            element={<CollectionSetLegacyRedirect />}
          />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundScreen />} />
    </Route>,
  ),
);
